import PropTypes from 'prop-types';
import React from 'react';
import Chart from 'react-apexcharts';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { createStructuredSelector } from 'reselect';
import { SCLoader } from 'rollup-allspark';
import {
  DEFAULT_CURRENCY_OBJ,
  // MONTHLY_TIME_PERIOD,
  WEEKLY_TIME_PERIOD,
} from '../../../config/constants/analytics';
import { getBaseChartOptions, mergeChartOptions } from '../theme/apexTheme';
import { COLORS } from '../theme/tokens';
import {
  formatDate,
  formatDateWithoutTimezone,
  getStartOfWeek,
} from '../../../utils/format-date';
import { centToMoney, readablePrice } from '../../../utils/getCurrency';
import {
  exportedCsvHeaderTitleForXAxis,
  findDateKey,
  getCumulativeSumForDailyData,
  getCumulativeSumForWeeklyData,
} from '../../../utils/nativeAnalyticsUtils';
import {
  makeSelectAnalyticsData,
  makeSelectAnalyticsDataLoading,
  makeSelectProperty,
  makeSelectTimePeriodSlug,
} from '../selectors';

const LineChart = ({
  targetKey,
  chartData,
  chartDataLoading,
  seriesName,
  selectedTimePeriod,
  selectedProvider,
}) => {
  // CONSTANTS
  const exportedCsvHeaderTitle =
    exportedCsvHeaderTitleForXAxis(selectedTimePeriod);
  const currencyCodeList =
    chartData && chartData.length ? chartData.map(b => b.currency) : [];
  const branchCurrencyObj =
    chartData && chartData.length
      ? {
          code: chartData[0].currency,
          symbol: chartData[0].currency_symbol,
        }
      : '';
  const shouldShowUsd = [...new Set(currencyCodeList)].length > 1;
  const currencyObj = shouldShowUsd ? DEFAULT_CURRENCY_OBJ : branchCurrencyObj;

  // CUMULATIVE DATA
  const getCumulativeSumForSelectedTimePeriod = () => {
    if (selectedTimePeriod === WEEKLY_TIME_PERIOD)
      return getCumulativeSumForWeeklyData(chartData, targetKey);
    // if (selectedTimePeriod === MONTHLY_TIME_PERIOD)
    //   return getCumulativeSumForMonthlyData(chartData, targetKey);
    return getCumulativeSumForDailyData(chartData, targetKey);
  };
  const cumulativeData = getCumulativeSumForSelectedTimePeriod() || [];

  const prepareXaxisData = data => {
    const dateKey = findDateKey([data]);
    if (selectedTimePeriod === WEEKLY_TIME_PERIOD) {
      const reportDate = new Date(data[dateKey]);
      const wStartDate = getStartOfWeek(reportDate);
      return formatDate(wStartDate.toISOString(), `MMM dd ''yy`);
    }
    // if (selectedTimePeriod === MONTHLY_TIME_PERIOD) {
    //   return formatDate(data[dateKey], `MMM ''yy`);
    // }
    return formatDateWithoutTimezone(data[dateKey], `MMM dd`);
  };

  // CHART DATA

  const xAxisData = cumulativeData.map(prepareXaxisData) || [];
  const numberOfProviders = selectedProvider.length;

  const yAxisData =
    cumulativeData.map(d => centToMoney(d.count / numberOfProviders)) || [];
  const chartConfig = {
    options: mergeChartOptions(getBaseChartOptions(), {
      colors: [COLORS.primary],
      markers: { size: 0, hover: { size: 5 } },
      stroke: { curve: 'smooth', width: 2.5 },
      fill: {
        type: 'gradient',
        gradient: { shadeIntensity: 0.3, opacityFrom: 0.25, opacityTo: 0.02 },
      },
      tooltip: {
        y: {
          formatter(value) {
            return readablePrice(value * 100, currencyObj);
          },
        },
      },
      chart: {
        targetKey,
        type: 'area',
        toolbar: {
          show: true,
          offsetX: 0,
          offsetY: 0,
          export: {
            csv: {
              filename: targetKey,
              headerCategory: exportedCsvHeaderTitle,
              headerValue: 'value',
            },
            svg: { filename: targetKey },
            png: { filename: targetKey },
          },
        },
      },
      xaxis: {
        type: 'category',
        categories: xAxisData,
      },
    }),
    series: [
      {
        name: seriesName,
        data: yAxisData,
      },
    ],
  };

  if (chartDataLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <SCLoader lineWidth={5} diameter={50} />
      </div>
    );
  }

  if (chartData && chartData.length) {
    return (
      <Chart
        options={chartConfig.options}
        series={chartConfig.series}
        type="area"
        height="350"
      />
    );
  }

  return (
    <div className="flex justify-center items-center h-full text-2xl text-grey-darker">
      No data found
    </div>
  );
};

LineChart.propTypes = {
  targetKey: PropTypes.string,
  seriesName: PropTypes.string,
  chartData: PropTypes.array,
  chartDataLoading: PropTypes.bool,
  selectedTimePeriod: PropTypes.string,
  selectedProvider: PropTypes.array,
};

const mapStateToProps = createStructuredSelector({
  chartDataLoading: makeSelectAnalyticsDataLoading(),
  chartData: makeSelectAnalyticsData(),
  selectedTimePeriod: makeSelectTimePeriodSlug(),
  selectedProvider: makeSelectProperty('selectedProvider'),
});

const withConnect = connect(mapStateToProps, null);
export default compose(withConnect)(LineChart);
